/**
 * Job-based submission uploader — Henry's two-phase rewrite.
 *
 *   1. POST /v2/submissions/upload  (multipart) → 202 { job_id, ... }
 *   2. GET  /v2/submissions/upload/{job_id}     → poll until terminal,
 *      then read `submission_id`.
 *
 * The backend classifies + extracts the uploaded files ASYNCHRONOUSLY in
 * a worker, so nothing is parsed when the POST returns — the FE polls the
 * job until it reaches a terminal state (`completed` / `partial` /
 * `failed`) and only then navigates. This mirrors the legacy OR-1292
 * flow (see ./underwriting-docs.ts) but against the new job endpoints;
 * the two live side-by-side behind the `job_upload` feature flag.
 */
import { getJsonReal, uploadMultipartReal } from "../client";

const BASE = "/v2/submissions/upload";

export type JobStatus = "queued" | "running" | "completed" | "partial" | "failed";
/** `stored` is a SUCCESS the backend added alongside `parsed`: the
 *  document was recognised and kept, it is just a type we deliberately
 *  do not extract (a licence, say). Reading it as a failure would tell
 *  the user their upload broke when it did exactly what it should. */
export type ParseTaskStatus = "pending" | "parsing" | "parsed" | "stored" | "failed";

export interface ParseTaskInfo {
    file_name: string;
    status: ParseTaskStatus;
    document_type: string | null;
    error: string | null;
    /** Per-file extraction payload (shape varies by document type). The
     *  same content is written onto the submission when the job finishes,
     *  so most callers can just re-fetch the submission instead. */
    extracted?: Record<string, unknown> | null;
}

/** 202 response from `POST /v2/submissions/upload`. */
export interface SubmissionUploadResponse {
    job_id: string;
    status: JobStatus;
    total_files: number;
}

/** Poll response from `GET /v2/submissions/upload/{job_id}`. */
export interface SubmissionUploadStatusResponse {
    job_id: string;
    status: JobStatus;
    /** Populated only when status is `completed` or `partial`; null otherwise. */
    submission_id: string | null;
    files: ParseTaskInfo[];
}

const TERMINAL: ReadonlySet<JobStatus> = new Set(["completed", "partial", "failed"]);

/**
 * Start a batch upload. Sends each file under the repeatable `files`
 * field plus an optional `title` (same multipart shape as the legacy
 * `createSubmissionFromUpload`). Returns the `job_id` to poll.
 */
export function startSubmissionUpload(files: File[], title?: string): Promise<SubmissionUploadResponse> {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    if (title) formData.append("title", title);
    return uploadMultipartReal<SubmissionUploadResponse>(BASE, formData);
}

export function getUploadJob(jobId: string): Promise<SubmissionUploadStatusResponse> {
    return getJsonReal<SubmissionUploadStatusResponse>(`${BASE}/${encodeURIComponent(jobId)}`);
}

/** 202 response from the per-submission attach POST. */
export interface SubmissionAttachResponse {
    submission_id: string;
    job_id: string;
    title: string | null;
    total_files: number;
}

/**
 * OR-1400 — attach ONE broker document to an EXISTING submission and
 * enqueue its extraction (`POST /v2/underwriting-docs/submissions/{id}`,
 * single `file` field per the backend route). The worker enriches the
 * submission in place when the job finishes; poll the returned `job_id`
 * on the same `GET /v2/submissions/upload/{job_id}` as the create flow.
 * N files → N calls → N single-file jobs.
 */
export function attachUnderwritingDoc(submissionId: string, file: File): Promise<SubmissionAttachResponse> {
    const formData = new FormData();
    formData.append("file", file);
    return uploadMultipartReal<SubmissionAttachResponse>(
        `/v2/underwriting-docs/submissions/${encodeURIComponent(submissionId)}`,
        formData,
    );
}

/**
 * Poll the job until it reaches a terminal state or the timeout elapses.
 * Resolves with the final status response, or `"timeout"` if it never
 * settles. `onPoll` fires after every successful poll (including non-terminal
 * ones) so callers can surface per-file progress. Transient GET errors are
 * swallowed — a single network blip shouldn't abort the post-upload flow —
 * so we keep retrying until the deadline. Runs inside the upload event
 * handler (not in render), hence a plain async loop rather than a hook. The
 * default timeout is generous: large multi-file batches with big loss runs
 * are chunked + parsed in parallel server-side and can take a while.
 */
export async function waitForUploadJob(
    jobId: string,
    opts: { intervalMs?: number; timeoutMs?: number; onPoll?: (job: SubmissionUploadStatusResponse) => void } = {},
): Promise<SubmissionUploadStatusResponse | "timeout"> {
    const intervalMs = opts.intervalMs ?? 7000;
    const timeoutMs = opts.timeoutMs ?? 300_000;
    const deadline = Date.now() + timeoutMs;
    for (;;) {
        try {
            const job = await getUploadJob(jobId);
            opts.onPoll?.(job);
            if (TERMINAL.has(job.status)) return job;
        } catch {
            // Transient — ignore and retry until the deadline.
        }
        if (Date.now() >= deadline) return "timeout";
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
}
